import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchTotalCountsAction } from "../../actions/dashboardActions";
import { toast } from "react-toastify";

export const dashboardStatsConfig = [
  {
    key: "totalClients",
    title: "Total Clients",
    iconName: "groupIcon",
    color: "#2563EB",
    filterKey: "entityCategory",
    filterValue: "client",
  },
  {
    key: "totalServicePartner",
    title: "Total Service Partners",
    iconName: "handshakeIcon",
    color: "#7C3AED",
    filterKey: "entityCategory",
    filterValue: "Service Partner",
  },
  {
    key: "totalPendings",
    title: "Documents Pending",
    iconName: "clockIcon",
    color: "#F59E0B",
    filterKey: "documentStatus",
    filterValue: "pending",
  },
  {
    key: "kycNotRequired",
    title: "KYC Not Required",
    iconName: "shieldOffIcon",
    color: "#F97316",
    filterKey: "requireKYC",
    filterValue: "false",
  },
  {
    key: "totalActive",
    title: "Total Active",
    iconName: "checkCircle2Icon",
    color: "#16A34A",
    filterKey: null,
    filterValue: null,
  },
  {
    key: "totalInactive",
    title: "Total Inactive",
    iconName: "xCircleIcon",
    color: "#DC2626",
    filterKey: null,
    filterValue: null,
  },
  {
    key: "availablePANCardNumbers",
    title: "Available PAN Numbers",
    iconName: "creditCardIcon",
    color: "#0891B2",
    filterKey: null,
    filterValue: null,
  },
  {
    key: "legacyUploadedCounts",
    title: "Legacy Uploaded",
    iconName: "cloudUploadIcon",
    color: "#9333EA",
    filterKey: null,
    filterValue: null,
  },
];
export const getFormattedStatValue = (value, isLoading) => {
  if (isLoading) return "...";
  if (value === null || value === undefined) return "0";
  return value.toString();
};

export const useDashboardCounts = () => {
  const [counts, setCounts] = useState({
    totalClients: 0,
    totalServicePartner: 0,
    totalPendings: 0,
    kycNotRequired: 0,
    kycSubmitted: 0,
    totalActive: 0,
    totalInactive: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoadingRef = useRef(false);

  const loadDashboardCounts = useCallback(
    async (cancelled = { current: false }) => {
      isLoadingRef.current = true;
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchTotalCountsAction();
        if (cancelled.current) return;
        if (response.success) {
          setCounts(response.data);
        } else {
          const msg = response.message || "Failed to load dashboard counts";
          setError(msg);
          toast.error(msg);
        }
      } catch {
        if (cancelled.current) return;
        toast.error("Failed to load dashboard counts");
      } finally {
        if (!cancelled.current) setIsLoading(false);
        isLoadingRef.current = false;
      }
    },
    [],
  );

  useEffect(() => {
    const cancelled = { current: false };
    loadDashboardCounts(cancelled);
    return () => {
      cancelled.current = true;
    };
  }, [loadDashboardCounts]);

  return { counts, isLoading, error, refetch: loadDashboardCounts };
};

export const getStatsArray = (counts, isLoading) => {
  return dashboardStatsConfig.map((config) => ({
    ...config,
    value: getFormattedStatValue(counts[config.key], isLoading),
  }));
};

export const useDashboard = () => {
  const { counts, isLoading } = useDashboardCounts();
  const stats = getStatsArray(counts, isLoading);
  const navigate = useNavigate();

  const handleCardClick = (stat) => {
    if (!stat.filterKey) return; // no navigation for info-only cards
    const query = `?${stat.filterKey}=${stat.filterValue}`;
    navigate(`/all-list${query}`);
  };

  return { stats, isLoading, handleCardClick };
};
